/* Color galleries for the Contour set selector. All changes stay within its product section. */
(() => {
  'use strict';

  const controllers = new WeakMap();
  const normalize = value => String(value || '').trim().toLowerCase();
  const all = (root, selector) => Array.from(root.querySelectorAll(selector));
  const mediaKey = element => String(element.dataset.contourMediaId
    || element.dataset.mediaId || element.dataset.target || '').split('-').pop();
  const mediaRank = (element, mediaOrder) => mediaOrder.get(mediaKey(element)) ?? Number.MAX_SAFE_INTEGER;

  function renumberAccessibleLabel(element, position) {
    const label = element.getAttribute('aria-label');
    if (label) element.setAttribute('aria-label', label.replace(/\d+/, String(position)));
    const hiddenLabel = element.querySelector('.visually-hidden');
    if (hiddenLabel?.textContent) {
      hiddenLabel.textContent = hiddenLabel.textContent.replace(/\d+/, String(position));
    }
  }

  function orderMediaSiblings(elements, mediaOrder) {
    const parents = new Map();
    elements.forEach(element => {
      if (!element.parentElement) return;
      const siblings = parents.get(element.parentElement) || [];
      siblings.push(element);
      parents.set(element.parentElement, siblings);
    });
    parents.forEach((siblings, parent) => {
      const ordered = siblings.map((element, index) => ({ element, index }))
        .sort((a, b) => mediaRank(a.element, mediaOrder) - mediaRank(b.element, mediaOrder) || a.index - b.index)
        .map(item => item.element);
      if (ordered.every((element, index) => element === siblings[index])) return;
      const slots = siblings.map(element => {
        const slot = document.createComment('');
        parent.replaceChild(slot, element);
        return slot;
      });
      slots.forEach((slot, index) => parent.replaceChild(ordered[index], slot));
    });
  }

  function orderGallery(gallery, mediaOrder) {
    orderMediaSiblings(all(gallery, '.product__media-item'), mediaOrder);
    orderMediaSiblings(all(gallery, '.thumbnail-list__item'), mediaOrder);
    const slidesByColor = new Map();
    all(gallery, '.product__media-item').forEach(slide => {
      const color = normalize(slide.dataset.alt);
      const slides = slidesByColor.get(color) || [];
      slides.push(slide);
      slidesByColor.set(color, slides);
    });
    const dots = all(gallery, '.slider-counter__link');
    const dotIndexes = new Map();
    dots.forEach(dot => {
      const color = normalize(dot.dataset.alt);
      const index = dotIndexes.get(color) || 0;
      const slide = slidesByColor.get(color)?.[index];
      dot.dataset.contourMediaId = slide ? mediaKey(slide) : '';
      dotIndexes.set(color, index + 1);
    });
    orderMediaSiblings(dots, mediaOrder);
  }
  const show = (element, visible) => {
    element.classList.toggle('hidden', !visible);
    element.hidden = !visible;
    element.setAttribute('aria-hidden', String(!visible));
  };
  const visibleSlides = slider => all(slider, '[id^="Slide-"]')
    .filter(slide => !slide.hidden && !slide.classList.contains('hidden'));

  function resetSingleSlider(slider, slides) {
    slider.sliderItemsToShow = slides;
    slider.sliderFirstItemNode = slides[0] || null;
    slider.sliderItemOffset = Math.max(slides[0]?.clientWidth || slider.slider?.clientWidth || 1, 1);
    slider.slidesPerPage = 1;
    slider.currentPage = slides.length ? 1 : 0;
    slider.totalPages = slides.length;
    if (slider.currentPageElement) slider.currentPageElement.textContent = String(slider.currentPage);
    if (slider.pageTotalElement) slider.pageTotalElement.textContent = String(slider.totalPages);
    [slider.prevButton, slider.nextButton].forEach(button => {
      if (button) button.disabled = true;
    });
    all(slider, '.slider-counter__link').forEach(dot => {
      const active = slides.length > 0 && !dot.hidden && !dot.classList.contains('hidden');
      dot.classList.toggle('slider-counter__link--active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  function prepareSlider(slider) {
    if (!slider || slider.contourGalleryPrepared) return;
    slider.contourGalleryPrepared = true;
    const nativeInit = slider.initPages;
    const nativeUpdate = slider.update;
    slider.update = function () {
      const slides = visibleSlides(this);
      if (slides.length < 2) {
        resetSingleSlider(this, slides);
        return;
      }
      if (slides.some(slide => slide.clientWidth > 0) && nativeUpdate) nativeUpdate.call(this);
    };
    slider.initPages = function () {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      if (this.sliderControlWrapper) {
        this.sliderControlLinksArray = all(this.sliderControlWrapper, '.slider-counter__link');
      }
      const slides = visibleSlides(this);
      this.sliderFirstItemNode = slides[0] || null;
      // The theme returns early for a singleton without clearing its previous page state.
      if (slides.length < 2) {
        resetSingleSlider(this, slides);
        return;
      }
      // Mobile-hidden thumbnails have no measurable layout until their ResizeObserver runs.
      if (!slides.some(slide => slide.clientWidth > 0)) return;
      if (nativeInit) nativeInit.call(this);
    };
    if (slider.slider) {
      slider.slider.addEventListener('scroll', event => {
        const slides = visibleSlides(slider);
        if (slides.length > 1) return;
        // The theme's original update callback was bound before this helper loaded.
        event.stopImmediatePropagation();
        resetSingleSlider(slider, slides);
      }, true);
    }
    slider.addEventListener('slideChanged', event => {
      const slide = event.detail?.currentElement;
      if (!slide || slide.hidden || slide.classList.contains('hidden')) event.stopImmediatePropagation();
    }, true);
  }

  function refreshSlider(slider, reset) {
    if (!slider) return;
    prepareSlider(slider);
    if (reset && slider.slider) slider.slider.scrollTo({ left: 0, top: 0, behavior: 'instant' });
    slider.initPages();
    const slides = visibleSlides(slider);
    if (slides.length > 1 && slides.some(slide => slide.clientWidth > 0) && slider.update) slider.update();
    const controls = slider.querySelector('.slider-buttons');
    if (controls) show(controls, slides.length > 1);
    else [slider.prevButton, slider.nextButton].forEach(button => {
      if (button) show(button, slides.length > 1);
    });
  }

  function selectedColor(section) {
    const picker = section.querySelector('variant-selects');
    return picker?.querySelector('input[name="Color"]:checked')?.value
      || picker?.querySelector('select[name="options[Color]"]')?.value
      || section.querySelector('aven-contour-sets')?.dataset.selectedColor;
  }

  function initialize(section) {
    if (!section || !section.querySelector('aven-contour-sets')) return null;
    if (controllers.has(section)) return controllers.get(section);
    if (!customElements.get('media-gallery') || !customElements.get('slider-component')) return null;
    const galleries = all(section, 'media-gallery');
    if (!galleries.length) return null;
    const mediaOrder = new Map((galleries[0].dataset.contourMediaOrder || '')
      .split(',').filter(Boolean).map((id, index) => [id, index]));
    const sectionId = section.id.slice('MainProduct-'.length);
    let currentColor = '';
    let frame = 0;

    galleries.forEach(gallery => {
      gallery.prependMedia = false;
      const nativeSetThumbnail = gallery.setActiveThumbnail;
      if (nativeSetThumbnail) {
        gallery.setActiveThumbnail = function (thumbnail) {
          // Ignore a delayed slideChanged event from the previous color group.
          if (!thumbnail || thumbnail.hidden || thumbnail.classList.contains('hidden')) return;
          nativeSetThumbnail.call(this, thumbnail);
        };
      }
      prepareSlider(gallery.querySelector('[id^="GalleryViewer-"]'));
      prepareSlider(gallery.querySelector('[id^="GalleryThumbnails-"]'));
    });

    function filter(color, reset) {
      const wanted = normalize(color);
      if (!wanted) return;
      const allowedIds = new Set();
      let activeRawId = '';
      galleries.forEach(gallery => {
        orderGallery(gallery, mediaOrder);
        const slides = all(gallery, '.product__media-item');
        const accepts = element => {
          const alt = normalize(element.dataset.alt);
          return alt === wanted || alt === 'always_display';
        };
        slides.forEach(slide => show(slide, accepts(slide)));
        all(gallery, '.thumbnail-list__item, .slider-counter__link').forEach(item => show(item, accepts(item)));
        all(gallery, '.slider-counter__link').filter(accepts).forEach((dot, index, dots) => {
          dot.setAttribute('aria-label', `Load image ${index + 1} of ${dots.length}`);
        });
        const visible = slides.filter(accepts);
        const active = (!reset && visible.find(slide => slide.classList.contains('is-active')))
          || visible.find(slide => normalize(slide.dataset.alt) === wanted) || visible[0];
        slides.forEach(slide => slide.classList.toggle('is-active', slide === active));
        all(gallery, '.thumbnail-list__item').filter(accepts).forEach((thumbnail, index) => {
          const button = thumbnail.querySelector('button');
          thumbnail.dataset.mediaPosition = String(index + 1);
          if (button) renumberAccessibleLabel(button, index + 1);
        });
        all(gallery, '.thumbnail-list__item').forEach(thumbnail => {
          const button = thumbnail.querySelector('button');
          if (!button) return;
          if (active && thumbnail.dataset.target === active.dataset.mediaId) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        visible.forEach((slide, index) => {
          allowedIds.add(slide.dataset.mediaId.slice(sectionId.length + 1));
          all(slide, '.product__media-toggle').forEach(button => renumberAccessibleLabel(button, index + 1));
        });
        if (active) activeRawId = active.dataset.mediaId.slice(sectionId.length + 1);
        const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
        const thumbnails = gallery.querySelector('[id^="GalleryThumbnails-"]');
        refreshSlider(viewer, reset);
        refreshSlider(thumbnails, reset);
        if (reset && active && viewer?.slider) {
          viewer.slider.scrollTo({ left: active.offsetLeft, top: 0, behavior: 'instant' });
        }
      });

      // ModalDialog moves this node to body, outside the original product section.
      const modal = document.getElementById(`ProductModal-${sectionId}`);
      if (modal) {
        orderMediaSiblings(all(modal, '.product-media-modal__content > [data-media-id]'), mediaOrder);
        all(modal, '.product-media-modal__content > [data-media-id]').forEach(media => {
          const visible = allowedIds.has(media.dataset.mediaId);
          show(media, visible);
          if (!visible || reset) media.classList.toggle('active', visible && media.dataset.mediaId === activeRawId);
        });
        if (reset) modal.querySelector('.product-media-modal__content')?.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      }
    }

    const controller = color => {
      if (!normalize(color)) return;
      const reset = normalize(color) !== currentColor;
      currentColor = normalize(color);
      if (reset && window.pauseAllMedia) window.pauseAllMedia();
      if (frame) cancelAnimationFrame(frame);
      filter(currentColor, reset);
      // Normalize once after the native picker's synchronous work and scheduled scroll.
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (section.isConnected) filter(currentColor, reset);
      });
    };
    controllers.set(section, controller);
    controller(section.dataset.contourGalleryColor || selectedColor(section));
    return controller;
  }

  function initializeAll() {
    all(document, 'aven-contour-sets').forEach(component => initialize(component.closest('[id^="MainProduct-"]')));
  }

  document.addEventListener('aven:contour-color', event => {
    const section = event.target.closest('[id^="MainProduct-"]');
    if (!section || !event.detail?.color) return;
    section.dataset.contourGalleryColor = event.detail.color;
    initialize(section)?.(event.detail.color);
  });
  document.addEventListener('change', event => {
    const picker = event.target.closest('variant-selects');
    if (!picker) return;
    const section = picker.closest('[id^="MainProduct-"]');
    const controller = initialize(section);
    if (controller) controller(selectedColor(section));
  });
  document.addEventListener('shopify:section:load', initializeAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  Promise.all(['media-gallery', 'slider-component'].map(name => customElements.whenDefined(name))).then(initializeAll);
})();
